"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, ChevronRight, X } from "lucide-react";

export default function Modal({
	isOpen,
	onClose,
	className,

	// Header
	title,
	description,
	showClose = true,
	onBack,

	// Body
	children,

	// Footer
	footer,
	primaryAction,
	secondaryAction,
	footerPosition = "center",

	// Settings
	size = "md",
	closeOnOutside = true,
	closeOnEsc = true,
}) {
	const modalRef = useRef(null);

	useEffect(() => {
		if (!closeOnEsc) return;

		const handleEsc = (e) => {
			if (e.key === "Escape") onClose?.();
		};

		document.addEventListener("keydown", handleEsc);

		return () => document.removeEventListener("keydown", handleEsc);
	}, [onClose, closeOnEsc]);

	useEffect(() => {
		document.body.style.overflow = isOpen ? "hidden" : "";

		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const sizes = {
		sm: "max-w-md",
		md: "max-w-xl",
		lg: "max-w-3xl",
		xl: "max-w-5xl",
		full: "full-screen"
	};

	const footerPositions = {
		left: "justify-end",
		center: "justify-center",
		right: "justify-start",
		between: "justify-between",
	}

	const hasHeader =
		title || description || showClose || onBack;

	const hasFooter =
		footer || primaryAction || secondaryAction;

	return createPortal(
		<div
			className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
			onMouseDown={(e) => {
				if (
					closeOnOutside &&
					modalRef.current &&
					!modalRef.current.contains(e.target)
				) {
					onClose?.();
				}
			}}
		>
			<div
				ref={modalRef}
				onMouseDown={(e) => e.stopPropagation()}
				className={`flex flex-col w-full bg-jmt-white border border-jmt-neutral-200 rounded-md shadow-full-light p-4 min-h-[35vh] max-h-[90vh] overflow-hidden
                  ${className}  ${sizes[size]} `} >

				{/* Header */}
				{hasHeader && (
					<div className="relative flex items-center justify-between border-b border-jmt-neutral-200 pb-4">

						{/* title & description */}
						{(title || description || onBack) && (
							<div className="text-right">
								<div className="flex items-center gap-2">
									{onBack && (
										<button
											type="button"
											onClick={onBack}
											aria-label="بازگشت"
											className="flex h-6 w-6 items-center justify-center text-jmt-neutral-700 hover:text-jmt-black transition cursor-pointer">
											{/* <ChevronRight size={20} /> */}
											<ArrowRight size={20} />
										</button>
									)}

									{title && (
										<h3 className="font-bold text-jmt-main">
											{title}
										</h3>
									)}
								</div>

								{description && (
									<p className="text-sm text-jmt-neutral-500 mt-1">
										{description}
									</p>
								)}

							</div>
						)}

						{/* close btn */}
						{showClose && (
							<button
								onClick={onClose}
								className=" text-jmt-neutral-700 hover:text-jmt-black transition cursor-pointer">
								<X size={22} />
							</button>
						)}

					</div>
				)}

				{/* Body */}
				<div className="flex-1 overflow-y-auto py-4 flex flex-col">
					{children}
				</div>

				{/* Footer */}
				{hasFooter && (
					<div className={`flex gap-2 mt-auto ${footerPositions[footerPosition]}`}>
						{footer ? (
							footer
						) : (
							<div className={`flex gap-2 mt-auto ${footerPositions[footerPosition]}`}>
								{primaryAction}

								{secondaryAction}

							</div>
						)}
					</div>
				)}
			</div>
		</div>,
		document.body
	);
}